import { apiClient } from "@/lib/apiClient";
import { PaginatedApiResponse } from "@/lib/apiClient";

export interface PermissionUser {
  id: string;
  name: string | null;
  email: string | null;
  phone_number: string;
  address?: {
    address_line_one: string;
    address_line_two: string;
    city: string;
    state: string;
    postal_code: string;
  };
  seller_stores?: Array<{
    store_id: string;
    role: string;
    status: string;
    created_at: string;
    updated_at: string;
  }>;
  access_level?: "seller" | "order_manager" | "staff";
  current_role?: "seller" | "order_manager" | "staff";
  role_assigned_at?: string;
  role_last_updated?: string;
  created_at: string;
  updated_at: string;
  last_active?: string;
  invited_by?: string;
}

export interface PermissionUsersResponse {
  data: PermissionUser[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface UpdateUserAccessLevelRequest {
  access_level: "seller" | "order_manager" | "staff";
}

export interface UpdateUserAccessLevelResponse {
  message: string;
  valid: boolean;
  data: PermissionUser;
}

export interface GetPermissionUsersParams {
  page?: number;
  page_size?: number;
  access_level?: "seller" | "order_manager" | "staff";
  sort_by?: string;
  sort_order?: "asc" | "desc";
  created_after?: string;
  created_before?: string;
}

export async function getPermissionUsers(
  storeId: string,
  params: GetPermissionUsersParams = {},
): Promise<PermissionUsersResponse> {
  try {
    const queryParams = new URLSearchParams();

    queryParams.append("page", (params.page || 1).toString());
    queryParams.append("page_size", (params.page_size || 10).toString());
    queryParams.append("sort_order", params.sort_order || "desc");

    if (params.access_level) {
      queryParams.append("access_level", params.access_level);
    }
    if (params.sort_by) {
      queryParams.append("sort_by", params.sort_by);
    }
    if (params.created_after) {
      queryParams.append("created_after", params.created_after);
    }
    if (params.created_before) {
      queryParams.append("created_before", params.created_before);
    }

    const response = await apiClient.getPaginated<PermissionUser>(
      `/api/v1/admin/${storeId}/settings/permissions/users?${queryParams.toString()}`,
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to fetch users");
    }

    return {
      data: response.data || [],
      pagination: {
        page: response.pagination?.page || params.page || 1,
        pageSize: response.pagination?.pageSize || params.page_size || 10,
        total: response.pagination?.total || 0,
        totalPages: response.pagination?.totalPages || 1,
      },
    };
  } catch (error) {
    console.error("Get permission users error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while fetching users");
  }
}

export async function updateUserAccessLevel(
  storeId: string,
  userId: string,
  accessLevel: "seller" | "order_manager" | "staff",
): Promise<PermissionUser> {
  try {
    const response = await apiClient.put<PermissionUser | UpdateUserAccessLevelResponse>(
      `/api/v1/admin/${storeId}/settings/permissions/users/${userId}`,
      { access_level: accessLevel },
    );

    if (!response.valid) {
      throw new Error(response.message || "Failed to update user access level");
    }

    const payload = response.data as any;
    return (payload?.data || payload) as PermissionUser;
  } catch (error) {
    console.error("Update user access level error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while updating user access level");
  }
}

export async function removeUserFromStore(
  storeId: string,
  userId: string,
): Promise<void> {
  try {
    const response = await apiClient.delete<{
      message: string;
      valid: boolean;
    }>(`/api/v1/admin/${storeId}/settings/permissions/users/${userId}`);

    if (!response.valid) {
      throw new Error(response.message || "Failed to remove user from store");
    }
  } catch (error) {
    console.error("Remove user from store error:", error);

    if (error instanceof Error) {
      throw error;
    }

    throw new Error("Network error occurred while removing user from store");
  }
}
